import { TABLE_SELECTOR } from './MobileTables.styled';

// Elementy nagłówka, których nie widać na ekranie: tekst dla czytników i ikony sortowania.
const HIDDEN_IN_HEADER = '.sr-only, [aria-hidden="true"]';

/*
 * Tekst komórki nagłówka bez treści ukrytej (`.sr-only`, `aria-hidden`). Kopia węzła zamiast
 * innerText: nagłówek na telefonie ma display: none, a innerText wymusza przeliczenie układu.
 */
export const headerCellText = (cell: HTMLTableCellElement): string => {
  if (!cell.querySelector(HIDDEN_IN_HEADER)) return (cell.textContent ?? '').trim();
  const copy = cell.cloneNode(true) as HTMLTableCellElement;
  copy.querySelectorAll(HIDDEN_IN_HEADER).forEach((node) => node.remove());
  return (copy.textContent ?? '').trim();
};

/*
 * Wiersz nagłówka z nazwami kolumn: ostatni widoczny wiersz thead z tekstem. HeroUI dokłada
 * na końcu thead pusty wiersz-odstęp (`aria-hidden`), który wcześniej był brany za nagłówek
 * i karty traciły podpisy pól.
 */
export const findHeaderRow = (table: HTMLTableElement): HTMLTableRowElement | undefined => {
  const rows = Array.from(table.tHead?.rows ?? []);
  const withLabels = rows.filter((row) => row.getAttribute('aria-hidden') !== 'true'
    && Array.from(row.cells).some((cell) => headerCellText(cell) !== ''));
  return withLabels[withLabels.length - 1] ?? rows[rows.length - 1];
};

/** Etykiety kolumn z wiersza nagłówka; komórka scalona (colSpan) daje etykietę każdej kolumnie. */
export const columnLabels = (table: HTMLTableElement): string[] => {
  const labels: string[] = [];
  Array.from(findHeaderRow(table)?.cells ?? []).forEach((cell) => {
    const text = headerCellText(cell);
    for (let i = 0; i < Math.max(1, cell.colSpan); i += 1) labels.push(text);
  });
  return labels;
};

interface CellState {
  cell: HTMLTableCellElement;
  empty: boolean;
  lead: boolean;
  title: boolean;
  /** Etykieta do zapisania; undefined, gdy komórka ma własne `data-label` z kodu widoku. */
  label: string | undefined;
}

/*
 * Stan jednego wiersza karty, liczony bez zapisów do DOM. Początkowe komórki bez tekstu
 * (checkbox, ikona, miniatura) stoją w jednym wierszu z tytułem karty, czyli pierwszą
 * komórką z tekstem.
 */
export const rowCellStates = (row: HTMLTableRowElement, labels: string[]): CellState[] => {
  const cells = Array.from(row.cells);
  let titleFound = false;
  let column = 0;
  return cells.map((cell, index) => {
    // Pole tekstowe (edycja nazwy w miejscu) liczy się jak tekst: inaczej w trakcie edycji
    // tytułem karty zostawała następna kolumna (np. data) i kafelek się rozjeżdżał.
    const hasTextInput = !!cell.querySelector('input[type="text"], input:not([type]), textarea');
    const hasText = (cell.textContent ?? '').trim() !== '' || hasTextInput;
    const lead = !titleFound && !hasText && cells.length > 1 && !!cell.querySelector('input, svg, img, button');
    const title = !titleFound && hasText;
    if (title) titleFound = true;
    if (index > 0 && !lead && !title && !titleFound) titleFound = true;
    // Komórka bez treści (np. akcje, których dla tego wiersza nie ma) nie zajmuje miejsca w karcie.
    const empty = !hasText && !cell.querySelector('input, button, a, svg, img, select, textarea, canvas');

    const columnLabel = labels[column] ?? '';
    column += Math.max(1, cell.colSpan);
    const ownLabel = cell.dataset.labelAuto === undefined && cell.hasAttribute('data-label');
    return { cell, empty, lead, title, label: ownLabel ? undefined : columnLabel };
  });
};

const toggle = (cell: HTMLTableCellElement, name: string, on: boolean): void => {
  if (on !== cell.hasAttribute(name)) cell.toggleAttribute(name, on);
};

const applyCellState = ({ cell, empty, lead, title, label }: CellState): void => {
  toggle(cell, 'data-mobile-empty', empty);
  toggle(cell, 'data-mobile-lead', lead);
  toggle(cell, 'data-mobile-title', title);
  if (label === undefined || cell.dataset.label === label) return;
  cell.setAttribute('data-label', label);
  cell.setAttribute('data-label-auto', '');
};

/*
 * Etykiety kolumn dla kart na telefonie. Style zamieniają wiersz tabeli w kartę, a każda
 * komórka pokazuje przed wartością nazwę swojej kolumny (`data-label`). Nazwy bierzemy z
 * nagłówka tabeli, żeby żaden z kilkudziesięciu widoków nie musiał ich podawać sam.
 * Komórka, która ma już `data-label` z kodu widoku, zostaje bez zmian.
 * Najpierw wszystkie odczyty, potem zapisy, żeby nie przeplatać ich z przeliczaniem układu.
 */
export const labelTables = (root: HTMLElement): void => {
  const states = Array.from(root.querySelectorAll<HTMLTableElement>(TABLE_SELECTOR)).flatMap((table) => {
    const labels = columnLabels(table);
    return Array.from(table.tBodies).flatMap((body) => Array.from(body.rows).flatMap((row) => rowCellStates(row, labels)));
  });
  states.forEach(applyCellState);
};
